import Phaser from 'phaser';
import { BuildingData } from '../socket/SocketManager';
import { IsoMap } from './IsoMap';

const BUILDING_COLORS: Record<string, number> = {
  HOUSE: 0xc8a882,
  APARTMENT: 0x9b8b7a,
  SHOP: 0xf4c842,
  POLICE_STATION: 0x3a6bc4,
  WAREHOUSE: 0x7a5c3a,
  PARK: 0x4caf50,
  CASINO: 0x7b2fbe,
  DEAL_POINT: 0x4a4a4a,
  WHOLESALER: 0x8b4513,
};

const BUILDING_HEIGHTS: Record<string, number> = {
  HOUSE: 40,
  APARTMENT: 70,
  SHOP: 35,
  POLICE_STATION: 55,
  WAREHOUSE: 45,
  PARK: 10,
  CASINO: 80,
  DEAL_POINT: 25,
  WHOLESALER: 50,
};

function darkenColor(color: number, factor: number): number {
  const r = Math.floor(((color >> 16) & 0xff) * factor);
  const g = Math.floor(((color >> 8) & 0xff) * factor);
  const b = Math.floor((color & 0xff) * factor);
  return (r << 16) | (g << 8) | b;
}

function lightenColor(color: number, factor: number): number {
  const r = Math.min(255, Math.floor(((color >> 16) & 0xff) * factor));
  const g = Math.min(255, Math.floor(((color >> 8) & 0xff) * factor));
  const b = Math.min(255, Math.floor((color & 0xff) * factor));
  return (r << 16) | (g << 8) | b;
}

export class IsoBuilding {
  private graphics: Phaser.GameObjects.Graphics;
  private label: Phaser.GameObjects.Text;
  public building: BuildingData;

  constructor(
    private scene: Phaser.Scene,
    building: BuildingData,
    private isoMap: IsoMap,
  ) {
    this.building = building;
    this.graphics = scene.add.graphics();
    const { x, y } = isoMap.gridToIso(building.gridX, building.gridY);
    const depth = building.gridX + building.gridY + 0.5;
    this.graphics.setDepth(depth);

    this.drawBuilding(x, y);

    this.label = scene.add
      .text(x, y - (BUILDING_HEIGHTS[building.type] ?? 30) - 10, building.name, {
        fontSize: '8px',
        fontFamily: 'monospace',
        color: '#ffffff',
        backgroundColor: '#00000088',
        padding: { x: 2, y: 1 },
      })
      .setOrigin(0.5)
      .setDepth(depth + 0.1)
      .setVisible(false);
  }

  private drawBuilding(cx: number, cy: number): void {
    const color = BUILDING_COLORS[this.building.type] ?? 0x888888;
    const height = BUILDING_HEIGHTS[this.building.type] ?? 30;
    const hw = 24; // Half-width of building footprint
    const hh = 12; // Half-height of iso footprint

    if (this.building.type === 'PARK') {
      // Parks are flat green areas with small trees
      this.graphics.fillStyle(0x4caf50, 1);
      this.graphics.beginPath();
      this.graphics.moveTo(cx, cy - hh);
      this.graphics.lineTo(cx + hw, cy);
      this.graphics.lineTo(cx, cy + hh);
      this.graphics.lineTo(cx - hw, cy);
      this.graphics.closePath();
      this.graphics.fillPath();

      // Small tree
      this.graphics.fillStyle(0x2e7d32, 1);
      this.graphics.fillCircle(cx, cy - 12, 6);
      this.graphics.fillStyle(0x5d4037, 1);
      this.graphics.fillRect(cx - 1, cy - 8, 2, 8);
      return;
    }

    const leftColor = darkenColor(color, 0.8);
    const rightColor = color;
    const topColor = lightenColor(color, 1.1);

    // Left face
    this.graphics.fillStyle(leftColor, 1);
    this.graphics.beginPath();
    this.graphics.moveTo(cx - hw, cy);
    this.graphics.lineTo(cx, cy + hh);
    this.graphics.lineTo(cx, cy + hh - height);
    this.graphics.lineTo(cx - hw, cy - height);
    this.graphics.closePath();
    this.graphics.fillPath();

    // Right face
    this.graphics.fillStyle(rightColor, 1);
    this.graphics.beginPath();
    this.graphics.moveTo(cx + hw, cy);
    this.graphics.lineTo(cx, cy + hh);
    this.graphics.lineTo(cx, cy + hh - height);
    this.graphics.lineTo(cx + hw, cy - height);
    this.graphics.closePath();
    this.graphics.fillPath();

    // Top face (flat roof)
    this.graphics.fillStyle(topColor, 1);
    this.graphics.beginPath();
    this.graphics.moveTo(cx, cy - hh - height);
    this.graphics.lineTo(cx + hw, cy - height);
    this.graphics.lineTo(cx, cy + hh - height);
    this.graphics.lineTo(cx - hw, cy - height);
    this.graphics.closePath();
    this.graphics.fillPath();

    // Windows for taller buildings
    if (height >= 35) {
      this.drawWindows(cx, cy, height, hw, hh);
    }

    // Owner indicator
    if (this.building.ownerId) {
      this.graphics.fillStyle(0x4caf50, 1);
      this.graphics.fillCircle(cx, cy - height - hh - 5, 3);
    }
  }

  private drawWindows(
    cx: number,
    cy: number,
    height: number,
    hw: number,
    hh: number,
  ): void {
    const windowColor = 0xffff88;
    const rows = Math.floor(height / 20);
    const cols = 2;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const wy =
          cy + hh - height + 10 + row * 18;
        // Right face windows
        const wx = cx + 5 + col * 10;
        this.graphics.fillStyle(windowColor, 0.6);
        this.graphics.fillRect(wx, wy, 5, 7);
      }
    }
  }

  showLabel(): void {
    this.label.setVisible(true);
  }

  hideLabel(): void {
    this.label.setVisible(false);
  }

  getGraphics(): Phaser.GameObjects.Graphics {
    return this.graphics;
  }
}
